import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  COUNTRY_DIAL_CODES,
  COUNTRY_NAMES_HE,
  detectCountryIsoFromE164,
  findCountryByDialCode,
  findCountryByIsoCode,
  normalizeContactPhoneE164,
  type CountryDialCode,
} from "@multi-chat/shared";
import { ChannelIcon } from "../../components/channel/ChannelIcon.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { ChevronDown, Lock, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";

export interface InternationalPhoneFieldProps {
  value: string;
  onChange: (e164: string) => void;
  onCountryIsoChange?: (iso: string) => void;
  defaultCountryIso?: string;
  disabled?: boolean;
  readOnly?: boolean;
  showLockIndicator?: boolean;
  id?: string;
  label?: string;
  numberTestId?: string;
  "data-testid"?: string;
}

function flagEmoji(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/[A-Z]/g, (letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)));
}

function splitE164(value: string, fallbackCountryIso: string): { countryIso: string; national: string } {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { countryIso: fallbackCountryIso, national: "" };
  }
  if (!trimmed.startsWith("+")) {
    return { countryIso: fallbackCountryIso, national: trimmed.replace(/\D/g, "") };
  }
  const country = findCountryByDialCode(trimmed);
  const countryIso = detectCountryIsoFromE164(trimmed) ?? country?.code ?? fallbackCountryIso;
  const dialCode = country?.dialCode ?? "";
  const national = dialCode ? trimmed.slice(dialCode.length).replace(/\D/g, "") : trimmed.slice(1);
  return { countryIso, national };
}

const unifiedControlClass =
  "flex h-9 min-w-0 items-stretch overflow-hidden rounded-lg border border-border bg-background focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-[color-mix(in_srgb,var(--color-accent)_55%,transparent)]";

export function InternationalPhoneField({
  value,
  onChange,
  onCountryIsoChange,
  defaultCountryIso = "IL",
  disabled = false,
  readOnly = false,
  showLockIndicator = false,
  id,
  label,
  numberTestId,
  "data-testid": dataTestId,
}: InternationalPhoneFieldProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const isHebrewUi = i18n.language === "he";
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [search, setSearch] = useState("");
  const numberRef = useRef<HTMLInputElement>(null);
  const [countryIso, setCountryIso] = useState(defaultCountryIso);
  const [national, setNational] = useState("");
  const fieldDisabled = disabled || readOnly;
  const numberId = id ?? "international-phone-number";

  useEffect(() => {
    const next = splitE164(value, defaultCountryIso);
    setCountryIso(next.countryIso);
    setNational(next.national);
  }, [value, defaultCountryIso]);

  const selectedCountry =
    findCountryByIsoCode(countryIso) ??
    findCountryByIsoCode(defaultCountryIso) ??
    COUNTRY_DIAL_CODES.find((country) => country.code === "IL");

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return COUNTRY_DIAL_CODES;
    return COUNTRY_DIAL_CODES.filter((country) => {
      const hebrewName = COUNTRY_NAMES_HE[country.code] ?? "";
      return (
        country.name.toLowerCase().includes(query) ||
        hebrewName.toLowerCase().includes(query) ||
        country.dialCode.toLowerCase().includes(query) ||
        country.dialCode.replace("+", "").includes(query) ||
        country.code.toLowerCase().includes(query)
      );
    });
  }, [search]);

  function emitChange(nextCountryIso: string, nextNational: string): void {
    const trimmed = nextNational.trim();
    onCountryIsoChange?.(nextCountryIso);
    if (trimmed.length === 0) {
      onChange("");
      return;
    }
    const result = trimmed.startsWith("+")
      ? normalizeContactPhoneE164({ raw: trimmed })
      : normalizeContactPhoneE164({
          raw: trimmed.replace(/\D/g, ""),
          defaultCountryIso: nextCountryIso,
        });
    onChange(result.ok ? result.e164 : trimmed);
  }

  function selectCountry(country: CountryDialCode): void {
    setPrefixOpen(false);
    setSearch("");
    setCountryIso(country.code);
    emitChange(country.code, national);
    requestAnimationFrame(() => numberRef.current?.focus());
  }

  if (readOnly && showLockIndicator) {
    return (
      <div className="grid min-w-0 gap-1 text-start" data-testid="whatsapp-locked-phone">
        <span className="text-[0.75rem] font-medium text-foreground">{t("phoneField.number")}</span>
        <div dir="ltr" className={cn(unifiedControlClass, "bg-surface-muted px-2.5")}>
          <span className="flex min-w-0 items-center gap-2">
            <span
              className="inline-grid size-5 shrink-0 place-items-center rounded-full bg-[#25D366]"
              aria-hidden="true"
            >
              <ChannelIcon platformType="WHATSAPP" size={10} className="text-white" />
            </span>
            <Lock size={13} className="shrink-0 text-muted" aria-hidden="true" />
            <LtrValue
              className="min-w-0 flex-1 truncate font-mono text-[0.8125rem] tabular-nums"
              title={value}
            >
              {value}
            </LtrValue>
            <span className="shrink-0 text-[0.6875rem] font-medium text-muted">
              {t("phoneField.whatsappLinked")}
            </span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={dataTestId ?? "international-phone-field"} className="grid min-w-0 gap-1">
      <label htmlFor={numberId} className="text-start text-[0.75rem] font-medium text-foreground">
        {label ?? t("phoneField.number")}
      </label>
      <div
        dir="ltr"
        className={cn(unifiedControlClass, fieldDisabled && "bg-surface-muted opacity-80")}
      >
        <Popover
          modal
          open={prefixOpen}
          onOpenChange={(open) => {
            if (fieldDisabled) return;
            setPrefixOpen(open);
            if (!open) setSearch("");
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={fieldDisabled}
              className="flex h-full shrink-0 items-center gap-1 border-e border-border bg-transparent px-2 text-[0.8125rem] disabled:opacity-70"
              aria-label={t("phoneField.country")}
              data-testid="international-phone-country"
            >
              <span aria-hidden="true">{selectedCountry ? flagEmoji(selectedCountry.code) : ""}</span>
              <span className="tabular-nums" dir="ltr">
                {selectedCountry?.dialCode ?? ""}
              </span>
              {showLockIndicator && fieldDisabled ? (
                <Lock size={13} className="shrink-0 text-muted" aria-hidden="true" />
              ) : (
                <ChevronDown size={13} className="shrink-0 opacity-60" aria-hidden="true" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            collisionPadding={12}
            className="flex w-[min(22rem,calc(100vw-1.5rem))] max-h-[min(20rem,var(--radix-popover-content-available-height))] flex-col overflow-hidden p-0"
          >
            <div className="flex shrink-0 items-center gap-2 border-b border-line px-2.5 py-2">
              <Search size={14} className="shrink-0 text-muted" aria-hidden="true" />
              <input
                className="min-w-0 flex-1 border-0 bg-transparent text-[0.8125rem] outline-none"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("phoneField.countrySearch")}
              />
            </div>
            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
              onWheel={(event) => event.stopPropagation()}
            >
              {filtered.map((country) => {
                const isSelected = country.code === selectedCountry?.code;
                const hebrewName = isHebrewUi ? COUNTRY_NAMES_HE[country.code] : undefined;
                return (
                  <button
                    key={country.code}
                    type="button"
                    className={cn(
                      "flex w-full min-w-0 items-center gap-2.5 overflow-hidden rounded-md px-2 py-1.5 text-start hover:bg-surface-muted",
                      isSelected ? "bg-surface-muted" : null,
                    )}
                    onClick={() => selectCountry(country)}
                  >
                    <span aria-hidden="true">{flagEmoji(country.code)}</span>
                    <span className="grid min-w-0 flex-1 gap-0.5">
                      <span className="truncate text-[0.8125rem]">{country.name}</span>
                      {hebrewName ? (
                        <span className="truncate text-[0.7rem] leading-tight text-muted">
                          {hebrewName}
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-[0.8125rem] tabular-nums text-muted" dir="ltr">
                      {country.dialCode}
                    </span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        <input
          ref={numberRef}
          id={numberId}
          data-testid={numberTestId}
          dir="ltr"
          inputMode="tel"
          readOnly={readOnly}
          disabled={disabled}
          className="min-w-0 flex-1 border-0 bg-transparent px-2.5 font-mono text-[0.8125rem] tabular-nums outline-none [unicode-bidi:isolate] placeholder:text-muted disabled:cursor-not-allowed"
          value={national}
          placeholder={t("phoneField.numberPlaceholder")}
          onChange={(event) => {
            const nextNational = event.target.value.replace(/[^\d+]/g, "");
            setNational(nextNational);
            emitChange(countryIso, nextNational);
          }}
        />
      </div>
    </div>
  );
}
